import { useState, type ChangeEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpen } from 'lucide-react'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Table,
  Textarea,
  type Column,
} from '@/components/ui'
import { PageHeader, QueryBoundary } from '@/components/domain'
import { ApiError, api } from '@/internationalization/api-client'
import { qk } from '@/internationalization/query-keys'
import { formatDayLabel } from '@/internationalization/format'
import { toast, useSessionStore } from '@/stores'
import { NotFoundPage } from '@/features/misc/NotFoundPage'
import type { ActiveVocabulary, VocabHistory, VocabVersion } from '@/types'

/** How many words the active list shows before "See more" — the full list
 *  runs to four figures and otherwise buries the controls below it. */
const PREVIEW_WORD_COUNT = 30

/** Comma- or newline-separated — the same split for typed text and an uploaded file. */
function parseWords(raw: string): string[] {
  return raw
    .split(/[,\n]/)
    .map((word) => word.trim())
    .filter(Boolean)
}

/**
 * The ONE global custom vocabulary Amazon Transcribe reads for every
 * workspace's jobs — there is no per-tenant copy, and adding a word here adds
 * it for every tenant's meetings, not just this one's.
 *
 * Restricted to three named people (`domain.vocab.VOCAB_ALLOWED_EMAILS`),
 * hardcoded on the server rather than gated by a permission, a role, or the
 * broader `sabha.platform_admins` mechanism. The nav item itself is hidden
 * from anyone else (`navigation.ts`'s `vocabularyOnly`), so reaching this page
 * at all already implies `canAccessVocabulary`. Read and write are the SAME
 * gate on the server -- all three named people may both see and edit the
 * list, with no separate platform-admin requirement -- so one flag covers
 * both the page's content and its controls here too.
 *
 * The controls below still check it again rather than assuming the guard
 * upstream held — the same fail-closed convention as every other gated
 * control in this app — and anyone else's read or write attempt gets the
 * API's 404 regardless. Hiding the controls is a convenience, never the
 * security boundary — that stays in the API.
 */
export function VocabularyPage() {
  const queryClient = useQueryClient()
  const [rawWords, setRawWords] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [showAllWords, setShowAllWords] = useState(false)
  const canAccessVocabulary = useSessionStore((s) => s.canAccessVocabulary)
  const canManage = canAccessVocabulary ?? false

  const active = useQuery({
    queryKey: qk.admin.vocabulary,
    queryFn: () => api.get<ActiveVocabulary>('/vocabulary'),
    enabled: canAccessVocabulary === true,
  })
  const history = useQuery({
    queryKey: qk.admin.vocabularyHistory,
    queryFn: () => api.get<VocabHistory>('/vocabulary/history'),
    enabled: canAccessVocabulary === true,
  })

  const invalidateVocabulary = () => {
    queryClient.invalidateQueries({ queryKey: qk.admin.vocabulary })
    queryClient.invalidateQueries({ queryKey: qk.admin.vocabularyHistory })
  }

  const addWords = useMutation({
    mutationFn: (words: string[]) => api.post<VocabVersion>('/vocabulary/words', { words }),
    onSuccess: (version) => {
      invalidateVocabulary()
      toast.success(
        `Vocabulary updated to version ${version.versionNumber}`,
        `${version.wordCount} word(s) in the active list.`,
      )
      setRawWords('')
      setProblem(null)
    },
    onError: (error) => {
      setProblem(
        error instanceof ApiError ? error.message : 'The vocabulary could not be updated.',
      )
    },
  })

  const restore = useMutation({
    mutationFn: (versionNumber: number) =>
      api.post<VocabVersion>(`/vocabulary/versions/${versionNumber}/restore`),
    onSuccess: (version) => {
      invalidateVocabulary()
      toast.success(`Restored as version ${version.versionNumber}`)
    },
    onError: (error) => {
      toast.error('Restore failed', error instanceof ApiError ? error.message : undefined)
    },
  })

  // Direct navigation to the URL bypasses the hidden nav item -- render the
  // same not-found state anyone else hitting an unknown route sees, rather
  // than a page whose queries are all refused. Reads as "not yet known" while
  // `/authorization/me` is in flight (`null`), never as "denied". This check
  // must come AFTER every hook call above (Rules of Hooks) -- the component
  // must call the same hooks in the same order on every render, including the
  // render where this flips from null to false.
  if (canAccessVocabulary === false) {
    return <NotFoundPage />
  }

  // The file path and the textarea path feed the SAME state, so "Add words"
  // always sends whatever is currently in the box — reading a file is just a
  // faster way to fill it in, not a second submission mechanism.
  const onFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = '' // lets the same file be picked again later
    if (!file) return
    setRawWords(await file.text())
  }

  const words = parseWords(rawWords)

  const columns: Column<VocabVersion>[] = [
    {
      id: 'version',
      header: 'Version',
      width: 'w-28',
      cell: (row) => (
        <span className="inline-flex items-center gap-2">
          v{row.versionNumber}
          {row.isActive && <Badge tone="success">Active</Badge>}
        </span>
      ),
    },
    { id: 'wordCount', header: 'Words', width: 'w-24', cell: (row) => row.wordCount },
    {
      id: 'createdAt',
      header: 'Created',
      width: 'w-32',
      cell: (row) => <span className="text-fg-muted">{formatDayLabel(row.createdAt)}</span>,
    },
    // Restoring is a write, so this column is dropped entirely for a viewer
    // who cannot manage the vocabulary rather than shown with every button
    // disabled.
    ...(canManage
      ? [
          {
            id: 'actions',
            header: '',
            align: 'right',
            cell: (row: VocabVersion) =>
              row.isActive ? null : (
                <Button
                  variant="ghost"
                  size="sm"
                  isLoading={restore.isPending && restore.variables === row.versionNumber}
                  onClick={() => restore.mutate(row.versionNumber)}
                >
                  Restore
                </Button>
              ),
          } satisfies Column<VocabVersion>,
        ]
      : []),
  ]

  return (
    <div>
      <PageHeader
        title="Vocabulary"
        description="Custom terms Amazon Transcribe should recognise across every workspace."
      />
      <div className="space-y-4">
        <Card>
          <CardHeader
            title={
              active.data?.version
                ? `Active vocabulary — version ${active.data.version.versionNumber}`
                : 'Active vocabulary'
            }
            description={
              active.data?.version
                ? `${active.data.version.wordCount} word(s)`
                : 'No words have been added yet.'
            }
          />
          <QueryBoundary isLoading={active.isPending} error={active.error} onRetry={active.refetch}>
            <CardBody>
              {active.data?.words.length ? (
                <div className="space-y-2">
                  <p className="text-base text-fg">
                    {(showAllWords
                      ? active.data.words
                      : active.data.words.slice(0, PREVIEW_WORD_COUNT)
                    ).join(', ')}
                    {!showAllWords && active.data.words.length > PREVIEW_WORD_COUNT && '…'}
                  </p>
                  {active.data.words.length > PREVIEW_WORD_COUNT && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAllWords((shown) => !shown)}
                    >
                      {showAllWords
                        ? 'See less'
                        : `See more (${active.data.words.length - PREVIEW_WORD_COUNT} more)`}
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-fg-muted">No words yet.</p>
              )}
            </CardBody>
          </QueryBoundary>
        </Card>

        {canManage && (
        <Card>
          <CardHeader
            title="Add words"
            description="Comma- or newline-separated, or upload a .txt file."
          />
          <CardBody className="space-y-3">
            {problem && (
              <Alert tone="danger" title="That did not work">
                {problem}
              </Alert>
            )}
            <Textarea
              placeholder="Sabha, Kubernetes, diarization…"
              value={rawWords}
              onChange={(event) => setRawWords(event.target.value)}
              rows={4}
            />
            <input
              type="file"
              accept=".txt"
              onChange={onFileSelected}
              className="block text-sm text-fg-muted file:mr-3 file:rounded-md file:border file:border-border file:bg-surface file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-fg hover:file:bg-surface-muted"
            />
            <div className="flex items-center justify-between">
              <span className="text-sm text-fg-subtle">
                {words.length} word{words.length === 1 ? '' : 's'} ready to add
              </span>
              <Button
                variant="primary"
                leftIcon={<BookOpen className="h-4 w-4" />}
                isLoading={addWords.isPending}
                disabled={words.length === 0}
                onClick={() => addWords.mutate(words)}
              >
                Add words
              </Button>
            </div>
          </CardBody>
        </Card>
        )}

        <Card>
          <CardHeader title="Version history" />
          <QueryBoundary isLoading={history.isPending} error={history.error} onRetry={history.refetch}>
            <Table
              columns={columns}
              rows={history.data?.versions ?? []}
              rowKey={(row) => String(row.versionNumber)}
              empty={
                <p className="px-4 py-10 text-center text-sm text-fg-muted">
                  No versions yet — add some words above.
                </p>
              }
            />
          </QueryBoundary>
        </Card>
      </div>
    </div>
  )
}
